<script setup>
import { ref, onMounted, onUnmounted, nextTick, watch } from "vue";
import api from "@/services/api";

/**
 * Props:
 * - plan (obj)
 * - user (obj)
 * - cpf  (string)
 * - couponCode (string)
 * - finalPrice (number)
 */
const props = defineProps({
  plan: { type: Object, required: true },
  user: { type: Object, required: true },
  cpf: { type: String, required: true },
  couponCode: { type: String, default: "" },
  finalPrice: { type: Number, default: null },
});

const emit = defineEmits(["subscription-result"]);

const isLoading = ref(true);
const error = ref(null);
const brickInstance = ref(null);
const pollingTimeoutId = ref(null);

let bricksBuilder;

// ✅ MUDANÇA: Helper para limpar a instância anterior do Mercado Pago
const unmountBrick = async () => {
  if (brickInstance.value) {
    await brickInstance.value.unmount();
    brickInstance.value = null;
  }
};

const clearPoll = () => {
  if (pollingTimeoutId.value) {
    clearTimeout(pollingTimeoutId.value);
    pollingTimeoutId.value = null;
  }
};

const formatCurrency = (value) => {
  return Number(value ?? 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
};

const buildPixResultPayload = (apiResult = {}, fallbackSuccess = true) => {
  const status = (apiResult?.status || "").toLowerCase();

  const creditAmount = Number(apiResult?.creditAmount ?? 0);
  const originalAmount = Number(
    apiResult?.originalAmount ?? props.plan?.price ?? 0
  );
  const chargedAmount = Number(apiResult?.chargedAmount ?? 0);

  const hasProportionalCredit = creditAmount > 0;

  const isFreeUpgrade =
    apiResult?.isFreeUpgrade === true || status === "free_upgrade";

  let message = "Você tem acesso ao plano por 30 dias.";

  if (hasProportionalCredit && isFreeUpgrade) {
    const creditCoveredExactly = creditAmount >= originalAmount;

    const coverageMessage = creditCoveredExactly
      ? "O crédito disponível na sua conta cobriu integralmente o valor desta assinatura."
      : "O crédito disponível na sua conta cobriu o valor desta assinatura, e o saldo restante era inferior a R$ 0,50 — valor abaixo do mínimo aceito para cobrança.";

    message = [
      "Plano atualizado com sucesso.",
      "",
      coverageMessage,
      "Por isso, nenhum pagamento foi processado e sua nova assinatura já está ativa.",
      "",
      `Valor da assinatura: ${formatCurrency(originalAmount)}`,
      `Crédito proporcional aplicado: ${formatCurrency(creditAmount)}`,
      `Valor pago agora: ${formatCurrency(0)}`,
    ].join("\n");
  } else if (hasProportionalCredit) {
    message = [
      "Crédito proporcional aplicado.",
      "",
      "Parte do valor da sua assinatura anterior foi aproveitada neste pagamento.",
      "",
      `Valor da assinatura: ${formatCurrency(originalAmount)}`,
      `Crédito proporcional aplicado: ${formatCurrency(creditAmount)}`,
      `Valor pago agora: ${formatCurrency(chargedAmount)}`,
    ].join("\n");
  }

  return {
    success: fallbackSuccess,
    ...apiResult,
    isFreeUpgrade,
    hasProportionalCredit,
    creditAmount,
    originalAmount,
    chargedAmount,
    message,
  };
};

const startPolling = (subscriptionId, apiResult = {}) => {
  clearPoll();

  const poll = async () => {
    try {
      // Consulta o MP diretamente e atualiza o banco
      const { data } = await api.post(`/payment/sync-latest-payment/${subscriptionId}`);

      const status = (data?.subscription?.status || "").toLowerCase();
      const paymentStatus = (data?.subscription?.payment_status || "").toLowerCase();

      if (status === "active" || paymentStatus === "approved") {
        // ✅ CORREÇÃO: o `apiResult` aqui é o `data` original do
        // create-pix-payment, capturado no submit, quando o status ainda era
        // "pending". Se emitíssemos esse status stale, o handleSubscriptionResult
        // do UpgradePage cairia no ramo "pending" e NÃO dispararia
        // trackSubscriptionCompletedOnce — mesmo o PIX já tendo sido aprovado.
        // Por isso sobrescrevemos com o status aprovado que o backend acabou de
        // confirmar neste poll.
        emit(
          "subscription-result",
          buildPixResultPayload(
            { ...apiResult, status: data?.subscription?.status || "active" },
            true
          )
        );
        return;
      }

      if (paymentStatus === "rejected" || paymentStatus === "cancelled") {
        emit("subscription-result", {
          success: false,
          ...apiResult,
          message: "Seu pagamento foi rejeitado ou cancelado.",
        });
        return;
      }

      pollingTimeoutId.value = setTimeout(poll, 8000);
    } catch (err) {
      console.error("Erro durante o polling PIX:", err);
      pollingTimeoutId.value = setTimeout(poll, 10000);
    }
  };

  poll();
};

const initializeBrick = async () => {
  if (!window.MercadoPago) {
    error.value = "SDK do Mercado Pago não foi carregada.";
    isLoading.value = false;
    return;
  }

  // ✅ MUDANÇA: Limpa qualquer Brick anterior antes de reinicializar com novo valor
  await unmountBrick();

  const YOUR_PUBLIC_KEY = process.env.VUE_APP_MP_PUBLIC_KEY;
  const mp = new window.MercadoPago(YOUR_PUBLIC_KEY, { locale: "pt-BR" });
  bricksBuilder = mp.bricks();

  // ✅ MUDANÇA: Define o valor final (com desconto) ou o preço original
  const amountToPay =
    props.finalPrice !== null ? props.finalPrice : Number(props.plan.price);

  brickInstance.value = await bricksBuilder.create(
    "payment",
    "pix-brick-container",
    {
      initialization: {
        amount: amountToPay,
        payer: { email: props.user?.email },
      },
      customization: {
        visual: { style: { theme: "default" } },
        paymentMethods: { bankTransfer: ["pix"] },
      },
      callbacks: {
        onReady: () => {
          isLoading.value = false;
        },
        onSubmit: async () => {
          // ==========================================================
          // ✅ MUDANÇA: trava duplo submit no PIX
          // ==========================================================
          if (isLoading.value) return;

          try {
            isLoading.value = true;

            const { data } = await api.post("/payment/create-pix-payment", {
              planId: props.plan.planId,
              payerEmail: props.user?.email,
              payerFirstName: props.user?.firstName,
              payerLastName: props.user?.lastName,
              payerIdentificationNumber: props.cpf,
              couponCode: props.couponCode,
            });

            const paymentId = data?.id;

            const subscriptionId =
              data?.externalReference || data?.newSubscriptionId;

            const status = (data?.status || "").toLowerCase();

            const isFreeUpgrade =
              data?.isFreeUpgrade === true || status === "free_upgrade";

            // Caso em que o crédito proporcional cobre tudo.
            // Não existe pagamento real no Mercado Pago.
            if (isFreeUpgrade) {
              await unmountBrick();

              emit("subscription-result", buildPixResultPayload(data, true));

              return;
            }

            // Caso normal: ainda haverá pagamento PIX.
            // Aqui o Mercado Pago precisa retornar paymentId e externalReference.
            if (!paymentId || !subscriptionId) {
              throw new Error(
                "API não retornou ID de Pagamento ou ExternalReference."
              );
            }

            await bricksBuilder.create(
              "statusScreen",
              "status-screen-container",
              {
                initialization: { paymentId: Number(paymentId) },
                callbacks: {
                  onReady: () => startPolling(subscriptionId, data),
                  onError: (err) => {
                    console.error("Erro no Status Screen:", err);
                    error.value =
                      err?.message || "Falha ao renderizar Status Screen.";
                  },
                },
              }
            );

            await unmountBrick();
          } catch (e) {
            console.error(e);
            error.value =
              e?.response?.data?.error ||
              e?.response?.data?.message ||
              e.message ||
              "Falha ao criar pagamento Pix.";
          } finally {
            isLoading.value = false;
          }
        },

        onError: (err) => {
          isLoading.value = false;
          error.value = err?.message || "Erro no Brick PIX.";
        },
      },
    }
  );
};

// ✅ MUDANÇA: Se o finalPrice mudar (cupom aplicado), reinicia o processo
watch(
  () => props.finalPrice,
  async () => {
    isLoading.value = true;
    await nextTick();
    await initializeBrick();
  }
);

onMounted(async () => {
  if (!props.plan || !props.user) {
    error.value = "Dados do plano/usuário não encontrados.";
    isLoading.value = false;
    return;
  }
  await nextTick();
  await initializeBrick();
});

onUnmounted(() => {
  unmountBrick();
  clearPoll();
});
</script>

<template>
  <div>
    <div id="pix-brick-container"></div>
    <div id="status-screen-container" class="mt-4"></div>

    <div v-if="isLoading" class="mt-4 text-center text-gray-500">
      Carregando formulário de pagamento...
    </div>

    <div v-if="error" class="mt-4 text-center text-red-600 font-medium">
      Erro: {{ error }}
    </div>
  </div>
</template>